import { api } from './client';
import type {
  AdminPasswordReset, Page, User, UserCreate, UserRole, UserUpdate, UUID,
} from './types';

export interface UsersListParams {
  /** One role or several (repeated query keys). */
  role?: UserRole | UserRole[];
  q?: string;
  /** Per-column filters on the Users list ("-" = blank, P41). */
  name_q?: string;
  email_q?: string;
  include_deleted?: boolean;
  /** P41: header sort, "key" or "-key" (server-side, across pages). */
  sort?: string;
  limit?: number;
  offset?: number;
}

function qs(params: object): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (Array.isArray(v)) for (const x of v) usp.append(k, String(x));   // repeated keys
    else if (v !== undefined && v !== null && v !== '') usp.set(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : '';
}

export const usersApi = {
  list:    (params: UsersListParams = {}) => api.get<Page<User>>(`/users${qs(params)}`),
  get:     (id: UUID)                     => api.get<User>(`/users/${id}`),
  create:  (payload: UserCreate)          => api.post<User>('/users', payload),
  update:  (id: UUID, payload: UserUpdate) => api.patch<User>(`/users/${id}`, payload),
  delete:  (id: UUID)                     => api.delete<User>(`/users/${id}`),
  restore: (id: UUID)                     => api.post<User>(`/users/${id}/restore`),
  /**
   * Admin force-reset of another user's password.
   * Backend signature: POST /users/{id}/password-reset?new_password=...
   * The new password is sent as a query string parameter (matches the FastAPI
   * route handler signature: `new_password: str` as a function arg becomes a
   * query param). Returns the User row.
   */
  adminPasswordReset: (id: UUID, payload: AdminPasswordReset) =>
    api.post<User>(`/users/${id}/password-reset?new_password=${encodeURIComponent(payload.new_password)}`),
  /**
   * Force log out a user from every device. Bumps users.session_epoch
   * on the server; the next request from any of their existing
   * sessions returns 401 because the cookie's epoch becomes stale.
   * Phase H0 launch hardening (#108).
   */
  forceLogout: (id: UUID) => api.post<User>(`/users/${id}/force-logout`),
};
